import { yupResolver } from "@hookform/resolvers/yup";
import type { ReactNode } from "react";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { Control, UseFormReturn } from "react-hook-form";
import { useFieldArray, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import type { AnyObjectSchema, AnySchema } from "yup";

import type { PersonSummary } from "../components";
import type {
  Configuration as KycConfiguration,
  SupportedCountry as KycSupportedCountry,
} from "../components/interface";
import type { SignerValidationByCountry } from "../utils";

export type KycFormFields<TPersonSummary = PersonSummary> = {
  ["company-info-reg-ein"]: string;
  ["company-info-vat"]: string;
  ["company-info-no-vat-number"]: boolean;
  ["company-info-reg-country"]: string;
  ["company-info-reg-state"]: string;
  ["company-info-company-number"]: string;
  ["company-info-company-type"]: string;
  ["company-info-company-incorporation-date"]: string;
  ["company-info-company-status"]: string;
  ["company-info-company-name"]: string;
  ["company-info-company-full-address"]: string;
  ["company-info-reg-same-address"]: boolean;
  ["company-info-op-address-1"]: string;
  ["company-info-op-address-2"]: string;
  ["company-info-op-state"]: string;
  ["company-info-op-city"]: string;
  ["company-info-op-zip"]: string;
  ["company-info-date-of-incorporation"]: string;
  ["company-info-website"]: string;
  ["company-info-primary-account-holder"]: string;
  ["company-info-registered-address-1"]: string;
  ["company-info-registered-address-2"]: string;
  ["company-info-registered-state"]: string;
  ["company-info-registered-city"]: string;
  ["company-info-registered-zip"]: string;
  ["associated-persons-no-ubos"]: boolean;
  ["associated-persons"]: TPersonSummary[];
};

export type KycPersonFields = {
  ["company-info-reg-country"]: string; // include so that validation can be country dependant
  ["person-id"]: number | null;
  ["person-first-name"]: string;
  ["person-middle-name"]: string;
  ["person-last-name"]: string;
  ["person-role"]: string;
  ["person-is-ubo"]: boolean;
  ["person-email"]: string;
  ["person-dob"]: string;
  ["person-birth-country"]: string;
  ["person-ssn"]: string;
  ["person-no-ssn"]: boolean;
  ["person-address-line1"]: string;
  ["person-address-line2"]: string;
  ["person-address-state"]: string;
  ["person-address-city"]: string;
  ["person-address-country"]: string;
  ["person-address-zip"]: string;
  ["person-birth-city"]: string;
  ["person-nationality"]: string;
  ["person-ubo-ownership-percentage"]: string;
  ["person-phone-number"]: string;
  ["person-phone-number-region-code"]: string;
  ["person-gender"]: string;
  ["person-tax-residency"]: string;
};

export type KycAirFields = {
  ["air-public-id"]: string;
  ["air-status"]: string;
  ["air-title"]: string;
  ["air-description"]: string;
};

export const COMPANY_OP_FIELDS: Record<string, keyof KycFormFields> = {
  address1: "company-info-op-address-1",
  address2: "company-info-op-address-2",
  state: "company-info-op-state",
  city: "company-info-op-city",
  zip: "company-info-op-zip",
};

export const COMPANY_REGISTERED_FIELDS: Record<string, keyof KycFormFields> = {
  address1: "company-info-registered-address-1",
  address2: "company-info-registered-address-2",
  state: "company-info-registered-state",
  city: "company-info-registered-city",
  zip: "company-info-registered-zip",
};

// N.B. For these two types the real signature should be the following:
// `UseFormReturn<[XXXFormFields], KycFormProviderRhfContext>`, so we describe the
// accurate context's shape we attach to these forms.
// However, doing so adds a lot of verbosity to the code as we have to add this context type
// to every usage of a ReactHookForm component, for no benefit (at the moment).
// So we choose to simplify it - and might add the correct context type later on if we need to.
export type KycForm = UseFormReturn<KycFormFields>;
export type KycPersonForm = UseFormReturn<KycPersonFields>;
export type KycAirForm = UseFormReturn<KycAirFields>;

export type ContractSignerForm = {
  updateValidation: () => boolean;
  error?: {
    message: string;
    title: string;
  };
  resetError: () => boolean;
};

export type KycForms = {
  /**
   * React hook form object for the KYC business form.
   */
  kycForm: KycForm;
  /**
   * Array of person objects that are present in the backend.
   */
  kycPersonFields: PersonSummary[];
  /**
   * React hook form object for the KYC person form.
   */
  personForm: KycPersonForm;
  /**
   * Function to update KYC person data with the react hook form object.
   */
  updateKycPerson: (id: number, changes: { [k in keyof PersonSummary]?: any }) => void;
  /**
   * Yup schema for the KYC business form.
   */
  kycValidation: AnySchema<KycValidationSchema>;
  /**
   * Yup schema for the KYC person form.
   */
  personValidation: AnySchema<PersonValidationSchema>;
  /**
   * Object containing functions for handling the contract signer logic.
   */
  contractSignerForm: ContractSignerForm;
};

export type KycValidationSchema = { [k in keyof KycFormFields]?: any };
export type PersonValidationSchema = { [k in keyof KycPersonFields]?: any };

const KycFormContext = createContext<KycForms | null>(null);

type RenderFunctionChild = (
  arg: Pick<KycForms, "kycForm" | "personForm" | "kycPersonFields" | "updateKycPerson">
) => ReactNode;
interface KycFormProviderProps {
  /**
   * Default values for the KYC business and person forms.
   */
  defaultKycValues: KycFormFields;
  /**
   * KYC configuration object for setting up the page context.
   */
  kycConfiguration: KycConfiguration;
  /**
   * Yup schema for the KYC business form.
   */
  kycValidation?: AnySchema<KycValidationSchema>;
  /**
   * Yup schema for the KYC person form.
   */
  personValidation?: AnySchema<PersonValidationSchema>;
  /**
   * Validation function for the contract signer.
   */
  contractSignerValidation: SignerValidationByCountry;
  /**
   * Children to render.
   */
  children: ReactNode | RenderFunctionChild;
}

export interface KycFormProviderRhfContext {
  kycConfiguration: KycConfiguration;
}

let testPersonValues = {};
try {
  testPersonValues = JSON.parse(localStorage.getItem("ADD_PERSON_DEFAULT_VALUES") as string);
  // eslint-disable-next-line no-empty
} catch {}

const KycFormProvider = ({
  defaultKycValues,
  kycValidation,
  personValidation,
  contractSignerValidation,
  kycConfiguration,
  children,
}: KycFormProviderProps) => {
  const intl = useIntl();
  const [contractSignerError, setContractSignerError] = useState<ContractSignerForm["error"]>();
  const countryCode = (defaultKycValues["company-info-reg-country"] || kycConfiguration.country) as KycSupportedCountry;
  const kycResolver = kycValidation ? yupResolver(kycValidation as AnyObjectSchema) : undefined;
  const personResolver = personValidation ? yupResolver(personValidation as AnyObjectSchema) : undefined;
  const formsContext: KycFormProviderRhfContext = { kycConfiguration };
  const kycForm = useForm<KycFormFields, KycFormProviderRhfContext>({
    mode: "onBlur",
    context: formsContext,
    shouldFocusError: true,
    defaultValues: defaultKycValues,
    resolver: kycResolver,
  });
  const personForm = useForm<KycPersonFields, KycFormProviderRhfContext>({
    mode: "onSubmit",
    shouldFocusError: true,
    resolver: personResolver,
    context: formsContext,
    defaultValues: {
      "person-id": null,
      "company-info-reg-country": countryCode,
      ...testPersonValues,
    },
  });
  const { update, replace } = useFieldArray<KycFormFields>({
    name: "associated-persons",
    // N.B. We have to cast the following type because `useFieldArray` is hard-coded to a `object` TContext
    control: kycForm.control as Control<KycFormFields<PersonSummary>>,
  });

  // control the entire field array, which means each onChange reflects on the fields object
  // see https://react-hook-form.com/api/usefieldarray
  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const watchPersonFields = kycForm.watch("associated-persons");
  const kycPersonFields = (watchPersonFields ?? []).map((field, index) => {
    return {
      ...field,
      ...watchPersonFields[index],
    };
  });

  const updateKycPerson = useCallback(
    (id, changes) => {
      const idx = kycPersonFields.findIndex(({ serverId }) => id === serverId);
      update(idx, {
        ...kycPersonFields[idx],
        ...changes,
      });
    },
    [kycPersonFields, update]
  );

  const contractSignerForm = useMemo(() => {
    const updateValidation = () => {
      const validate = contractSignerValidation?.validate(kycPersonFields, intl);
      setContractSignerError(validate?.error);
      return validate.isValid;
    };
    const resetError = () => {
      const validate = contractSignerValidation?.validate(kycPersonFields, intl);
      setContractSignerError(undefined);
      return validate.isValid;
    };
    return {
      updateValidation,
      resetError,
      error: contractSignerError,
    };
  }, [contractSignerError, contractSignerValidation, intl, kycPersonFields]);

  // server state should always win
  useEffect(() => {
    const updatedPersons: PersonSummary[] = [];

    // I dislike this, but we need a way to make sure we don't override the idv status
    // with a stale value from the server, i.e. react-query refreshes before the back-end
    // gets the webhook from stripe to say that the request is processing.
    // This checks if the server is trying to override the client processing state with the
    // initial state, which is generally unexpected, and keeps the client version.
    // @todo: should this live in customer app?
    for (const newPerson of defaultKycValues["associated-persons"]) {
      const hasInitialState =
        newPerson.idvSessionStatus?.status === "requires_input" && !newPerson.idvSessionStatus?.last_error;
      const existing = kycPersonFields.find(({ serverId }) => serverId === newPerson.serverId);
      if (hasInitialState && existing && existing.idvSessionStatus?.status !== "requires_input") {
        updatedPersons.push({ ...newPerson, idvSessionStatus: existing.idvSessionStatus });
      } else {
        updatedPersons.push({ ...newPerson });
      }
    }
    replace(updatedPersons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultKycValues, replace]);

  const contextValue = {
    // Let's simplify the types byt intentionally dropping the TContext type.
    // Wel''l remove this simplification later on if we have to.
    kycForm: kycForm as KycForm,
    personForm: personForm as KycPersonForm,
    kycValidation: kycValidation as AnySchema<KycValidationSchema>,
    personValidation: personValidation as AnySchema<PersonValidationSchema>,
    contractSignerForm,
    kycPersonFields,
    updateKycPerson,
  };
  return (
    <KycFormContext.Provider value={contextValue}>
      <>
        {typeof children === "function"
          ? children({ kycForm, personForm, kycPersonFields, updateKycPerson })
          : children}
      </>
    </KycFormContext.Provider>
  );
};

export { KycFormContext, KycFormProvider };
